import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button, Container, Grid, makeStyles, Typography } from '@material-ui/core';
import ReCAPTCHA from 'react-google-recaptcha';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { Link } from 'react-router-dom';

import LoginHeader from './LoginHeader';
import User from '../../models/User';
import useQueryParams from '../../utils/useQueryParams.hook';
import EditFields from '../common/EditFields';
import LoadingButton from '../common/LoadingButton';
import Footer from '../footer/Footer';

const useStyles = makeStyles((theme) => {
  return {
    buttonContainer: {
      marginTop: '20px',
      position: 'relative',
    },
    errorMessage: {
      color: '#B12A2A',
      marginTop: '1rem',
    },
    fieldContainer: {
      margin: '0px 0px 20px',
    },
    formTitle: {
      color: '#172B4D',
      fontSize: '30px',
      fontWeight: 'bold',
      marginBottom: '16px',
    },
    link: {
      color: theme.palette.primary.main,
    },
    loginHint: {
      color: '#999999',
      fontSize: '12px',
      lineHeight: '22px',
      marginBottom: '30px',
    },
    loginLabels: {
      fontWeight: 'bold',
    },
    logo: {
      display: 'flex',
      marginBottom: '3rem',
      textAlign: 'unset',
    },
    mainContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      maxWidth: '410px',
      paddingBottom: '42px',
      paddingTop: '42px',
    },
    orBorder: {
      borderTop: '1px solid #E7E7E7',
      height: '0px',
    },
    orTypography: {
      color: '#999999',
      fontSize: '14px',
      lineHeight: '24px',
      margin: '0px 12px',
    },
    parentContainer: {
      display: 'grid',
      minHeight: '100vh',
    },
    passwordLink: {
      fontSize: '14px',
      marginBottom: '1rem',
      marginTop: '-8px',
    },
    textDash: {
      width: '100%',
    },
  };
});

const defaultData = { email: '', password: '' };

export default ({ isMediphanyWorkspace, isLoggingIn$, onSubmit, errorMessage, onRegister, validationErrors, onTokenLogin, useRecaptcha = false }) => {
  const classes = useStyles();
  const editFieldRef = useRef();
  const query = useQueryParams();
  const [recaptchaV2Token, setRecaptchaV2Token] = useState(null);

  const fields = useMemo(
    () => [
      {
        containerClass: classes.fieldContainer,
        label: 'Email Address',
        margin: 'normal',
        name: 'email',
        placeholder: 'Email Address',
        required: true,
        type: 'text',
        variant: 'standard',
      },
      {
        containerClass: classes.fieldContainer,
        hidePasswordIcon: true,
        label: 'Password',
        margin: 'normal',
        name: 'password',
        placeholder: '********',
        required: true,
        type: 'password',
        variant: 'standard',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Enter key pressed
  const handleSave = (data) => {
    onSubmit(data);
  };

  // Click submit button
  const handleSubmit = async () => {
    const validData = await editFieldRef.current.getValidData();

    if (useRecaptcha && !recaptchaV2Token) return;

    if (validData) {
      onSubmit(validData, recaptchaV2Token);
    }
  };

  const handleTokenLogin = (token, isDemo) => {
    if (onTokenLogin && typeof onTokenLogin === 'function') {
      onTokenLogin(token, isDemo);
    }
  };

  const renderError = () => {
    return errorMessage ? (
      <Grid item>
        <Typography align="left" variant="body1" className={classes.errorMessage}>
          {errorMessage}
        </Typography>
      </Grid>
    ) : null;
  };

  useEffect(() => {
    if (query.token) {
      const isDemo = query.demo === 'true';
      handleTokenLogin(query.token, isDemo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onVerify = useCallback((token) => {
    setRecaptchaV2Token(token);
  }, []);

  return (
    <div className={classes.parentContainer}>
      {/* Header */}
      <div style={{ marginBottom: 'auto' }}>
        <LoginHeader />
      </div>

      {/* Content */}
      <Container component="main" className={classes.mainContainer}>
        <Container component="div" disableGutters>
          <Typography variant="h4" className={`${classes.formTitle}`} align="center">
            Portal Login
          </Typography>
          <Typography variant="body1" className={`${classes.loginHint}`} align="center">
            Log in by entering your details below. If you do not have an account please register by using the registration option below.{' '}
          </Typography>
        </Container>

        <form>
          <Grid container direction="column" justify="flex-end">
            <Grid item>
              <EditFields
                data={defaultData}
                fields={fields}
                onSave={handleSave}
                validate={User.validateCreate}
                useLabel
                allowEnterSubmit
                ref={editFieldRef}
                labelPosition="top"
                labelClass={classes.loginLabels}
                validationErrors={validationErrors}
              />
            </Grid>
            <Grid item>
              <Typography variant="body1" align="right" className={`${classes.passwordLink} ${classes.link}`}>
                <Link to="/forgot-password">Forgot password?</Link>
              </Typography>
            </Grid>

            <Grid item style={{ position: 'relative' }}>
              <GoogleReCaptchaProvider reCaptchaKey={process.env.REACT_APP_RECAPTCHA_KEY}>
                {useRecaptcha && <ReCAPTCHA sitekey={process.env.REACT_APP_V2_RECAPTCHA_KEY} onChange={onVerify} />}
              </GoogleReCaptchaProvider>
            </Grid>

            <Grid item className={classes.buttonContainer}>
              <LoadingButton variant="contained" color="primary" fullWidth onClick={handleSubmit} loading$={isLoggingIn$}>
                Login
              </LoadingButton>
            </Grid>
            {isMediphanyWorkspace && (
              <>
                <Grid item className={classes.buttonContainer}>
                  <Grid container alignItems="center" style={{ flexWrap: 'nowrap' }}>
                    <Grid item style={{ flexGrow: '1' }}>
                      <div className={classes.orBorder} />
                    </Grid>
                    <Grid item>
                      <Typography className={classes.orTypography}>or</Typography>
                    </Grid>
                    <Grid item style={{ flexGrow: '1' }}>
                      <div className={classes.orBorder} />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item className={classes.buttonContainer}>
                  <Button variant="outlined" color="primary" fullWidth onClick={onRegister}>
                    Create Account
                  </Button>
                </Grid>
              </>
            )}
            {renderError()}
          </Grid>
        </form>
      </Container>

      {/* Footer */}
      <div style={{ marginTop: 'auto' }}>
        <Footer />
      </div>
    </div>
  );
};
